/**
 * Theme toggle — light (default) or dark.
 *
 * Persists choice in localStorage. On first visit:
 *   - If localStorage has a value → use it
 *   - Else → default to light
 *
 * The <html> element gets the class only in dark mode:
 *   <html>          → light
 *   <html class="dark"> → dark
 *
 * (Tailwind's `darkMode: 'class'` strategy — see tailwind.config.js)
 *
 * Why no system preference fallback?
 *   The user explicitly said default is light. They can switch to dark
 *   from the toggle and it persists across visits.
 */

const STORAGE_KEY = 'datames_theme';

export function getStoredTheme() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark') return v;
  } catch {
    /* localStorage unavailable (e.g. private mode) — ignore */
  }
  return null;
}

export function getEffectiveTheme() {
  return getStoredTheme() || 'light';
}

export function applyTheme(theme) {
  const root = document.documentElement;
  if (theme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

export function toggleTheme() {
  const current = getEffectiveTheme();
  const next = current === 'dark' ? 'light' : 'dark';
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    /* ignore */
  }
  applyTheme(next);
  // Notify any in-page listeners (theme toggle buttons re-render)
  window.dispatchEvent(new CustomEvent('themechange', { detail: { theme: next } }));
  return next;
}

/**
 * Run BEFORE React mounts — sets the <html> class so the first paint
 * already uses the right theme. No flash of wrong theme.
 */
export function initTheme() {
  applyTheme(getEffectiveTheme());
}

export const THEME_STORAGE_KEY = STORAGE_KEY;